import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { getMessageList, type MessageRecord } from '@/api/modules/business/message';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

interface MessagePreviewProps {
  children: React.ReactNode;
}

interface PreviewMessage {
  id: string;
  title: string;
  content: string;
  time: string;
  read: boolean;
}

export function MessagePreview({ children }: MessagePreviewProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<PreviewMessage[]>([]);
  const navigate = useNavigate();

  const fetchUnreadMessages = async () => {
    try {
      const res = await getMessageList({
        pageSize: 999,
        pageNum: 1,
        object: {
          state: '0' // 未读
        }
      });
      
      if (res.code === 0 && res.data) {
        const mappedMessages = res.data.records.map((record: MessageRecord) => ({
          id: record.messageId,
          title: record.title,
          content: record.body,
          time: record.createTimes || '',
          read: false
        }));
        setMessages(mappedMessages);
      }
    } catch (error) {
      console.error('Fetch preview messages error:', error);
    }
  };

  useEffect(() => {
    fetchUnreadMessages();
  }, []);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      fetchUnreadMessages();
    }
  };

  const handleMarkAllRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMessages([]);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div
          className="w-full outline-none relative cursor-pointer"
        >
          {children}
          {messages.length > 0 && (
            <span className="absolute top-1.5 left-4 block h-1.5 w-1.5 rounded-full bg-red-600" />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="end"
        className="w-80 p-0 overflow-hidden"
        sideOffset={5}
        alignOffset={-10}
      >
        <div className="flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/50">
            <h4 className="font-medium text-sm">消息中心</h4>
            <Button
                variant="link" 
                className="text-blue-500 hover:text-blue-600 p-0 h-auto font-normal"
                onClick={handleMarkAllRead}
                disabled={messages.length <= 0}
            >
                全部标记为已读
            </Button>
          </div>
          
          <div className="flex-1 overflow-y-auto max-h-[300px] min-h-[100px]">
             {messages.length > 0 ? (
               messages.map((msg) => (
                 <div 
                   key={msg.id} 
                   className="px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors border-b last:border-0 border-gray-100 group"
                   onClick={() => {
                       setIsOpen(false);
                       navigate('/message-center');
                   }}
                 >
                   <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium truncate pr-2 group-hover:text-blue-600 transition-colors">{msg.title}</span>
                      <span className="text-xs text-gray-400 whitespace-nowrap">{msg.time}</span>
                   </div>
                   <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                     {msg.content}
                   </p>
                 </div>
               ))
             ) : (
               <div className="flex flex-col items-center justify-center h-full py-8 text-gray-400">
                 <p className="text-sm">暂无未读消息</p>
               </div>
             )}
          </div>
          <div className="p-2 border-t text-right">
            <span 
              className="text-xs text-gray-500 cursor-pointer hover:text-blue-600 transition-colors"
              onClick={() => {
                setIsOpen(false);
                navigate('/message-center');
              }}
            >
              历史消息
            </span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
